import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { filterPillClass } from '../components/FilterPill';
import { StatusPill } from '../components/room/StatusPill';
import { useMe } from '../lib/auth';
import { templateExportCsvUrl } from '../lib/exports';
import { adminRoomsQuery, type RoomStatus, type RoomSummary } from '../lib/rooms';
import { templatesQuery } from '../lib/templates';

type StatusFilter = RoomStatus | 'ALL';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'LIVE', label: 'Live' },
  { key: 'FINISHED', label: 'Finished' },
  { key: 'ARCHIVED', label: 'Archived' },
];

function AdminRoomRow({ room }: { room: RoomSummary }) {
  return (
    <Link
      to={`/rooms/${room.id}`}
      className="grid min-h-tap grid-cols-1 items-center gap-3 rounded-lg border border-border px-5 py-4 transition-colors hover:border-accent-border md:grid-cols-[1fr_auto_auto_auto]"
    >
      <div className="min-w-0">
        <span className="display block truncate text-2xl">{room.className}</span>
        <span className="block truncate text-sm text-text-muted">{room.guestLabel}</span>
      </div>
      <span className="eyebrow hidden md:block">{room.seriesLabel}</span>
      <span className="telemetry hidden text-text-faint md:block">{room.templateKey}</span>
      <StatusPill status={room.status} />
    </Link>
  );
}

/**
 * ADMIN-only room roster: every room in the system (not just the caller's own), filterable by
 * status/template, plus a "Download season CSV" per template — the template-wide export from
 * `docs/specs/WP8-exports.md`. A non-admin who somehow lands here (e.g. a stale bookmark) is
 * bounced to `/` rather than shown a 403 page.
 */
export function AdminRooms() {
  const { data: me } = useMe();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [templateFilter, setTemplateFilter] = useState<number | 'ALL'>('ALL');

  const isAdmin = me?.role === 'ADMIN';

  const { data: templates } = useQuery({ ...templatesQuery, enabled: isAdmin });
  const {
    data: rooms,
    isPending,
    error,
  } = useQuery({
    ...adminRoomsQuery({
      ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
      ...(templateFilter !== 'ALL' ? { templateId: templateFilter } : {}),
    }),
    enabled: isAdmin,
  });

  if (!me) {
    return null;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
        <span className="eyebrow">02 / Admin</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <h1 className="display reveal mt-4 text-[clamp(3.5rem,10vw,8rem)]" style={{ ['--i' as string]: 1 }}>
        Every room
      </h1>

      <div className="reveal mt-8 flex flex-wrap items-center gap-2" style={{ ['--i' as string]: 2 }}>
        <div role="group" aria-label="Status filter" className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              aria-pressed={statusFilter === filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={filterPillClass(statusFilter === filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <label className="ml-auto flex items-center gap-2 text-sm text-text-muted">
          <span className="eyebrow">Template</span>
          <select
            value={templateFilter}
            onChange={(event) => setTemplateFilter(event.target.value === 'ALL' ? 'ALL' : Number(event.target.value))}
            className="min-h-tap rounded-md border border-border-strong bg-bg px-2 text-sm text-text"
          >
            <option value="ALL">All templates</option>
            {(templates ?? []).map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {templates && templates.length > 0 && (
        <div className="reveal mt-6 flex flex-wrap gap-2" style={{ ['--i' as string]: 3 }}>
          {templates.map((template) => (
            <a
              key={template.id}
              href={templateExportCsvUrl(template.id)}
              download
              className="min-h-tap inline-flex items-center rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted transition-colors hover:border-accent-border hover:text-text"
            >
              Download season CSV — {template.name}
            </a>
          ))}
        </div>
      )}

      {isPending && <p className="reveal telemetry mt-10 text-text-faint">Loading rooms…</p>}
      {error && (
        <p role="alert" className="reveal telemetry mt-10 text-signal">
          Could not load rooms. Please retry in a moment.
        </p>
      )}
      {rooms && rooms.length === 0 && (
        <p className="reveal mt-10 telemetry text-text-faint">No rooms match these filters.</p>
      )}

      {rooms && rooms.length > 0 && (
        <ul className="reveal mt-8 flex flex-col gap-2" style={{ ['--i' as string]: 4 }}>
          {rooms.map((room) => (
            <li key={room.id}>
              <AdminRoomRow room={room} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
